import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Checkbox, Collapse } from "@mui/material";
import { ApiService, DistributionNotificationService } from "../../../../../../api";
import { CustomButton, IconComponent, icon } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import styles from "./TerritorySelector.module.css";

const extractId = (item) => {
	if (typeof item === "number") {
		return item;
	}
	if (typeof item === "object" && item !== null) {
		return item.id;
	}
	return null;
};

export default function TerritorySelector({ projectId, selectedTerritories = [], onChange, defaultTerritoryId }) {
	const [territories, setTerritories] = useState([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [isExpanded, setIsExpanded] = useState(false);
	const [pendingSelection, setPendingSelection] = useState([]);
	const [hasInteracted, setHasInteracted] = useState(false);
	const cancelTokenSourceRef = useRef(null);
	const searchInputRef = useRef(null);

	useEffect(() => {
		if (isExpanded && searchInputRef.current) {
			searchInputRef.current.focus();
		}
		if (isExpanded) {
			setHasInteracted(false);
		}
	}, [isExpanded]);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		if (projectId) {
			setLoading(true);
			DistributionNotificationService.getTerritories(projectId, cancelTokenSourceRef.current?.token)
				.then((data) => {
					setTerritories(data || []);
				})
				.catch((err) => {
					console.error("Error fetching territories:", err);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [projectId]);

	useEffect(() => {
		if (territories.length > 0 && selectedTerritories.length > 0) {
			const validSelection = selectedTerritories.filter((t) => {
				const id = extractId(t);
				const territory = territories.find((ter) => ter.id === id);
				return territory?.hasDistributionList;
			});
			setPendingSelection(validSelection);
			if (validSelection.length !== selectedTerritories.length) {
				onChange(validSelection);
			}
		} else {
			setPendingSelection(selectedTerritories);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [territories, selectedTerritories]);

	const selectedIds = useMemo(() => selectedTerritories.map(extractId).filter(Boolean), [selectedTerritories]);

	const defaultIds = useMemo(() => (defaultTerritoryId ? [defaultTerritoryId] : []), [defaultTerritoryId]);

	const pendingIds = useMemo(() => pendingSelection.map(extractId).filter(Boolean), [pendingSelection]);

	const filteredTerritories = useMemo(() => {
		let result = territories;
		if (search && search.length >= 2) {
			const lowerSearch = search.toLowerCase();
			result = result.filter((t) => t.name?.toLowerCase().includes(lowerSearch));
		}
		return [...result].sort((a, b) => {
			const aDefault = defaultIds.includes(a.id);
			const bDefault = defaultIds.includes(b.id);
			if (aDefault && !bDefault) {
				return -1;
			}
			if (!aDefault && bDefault) {
				return 1;
			}
			const aSelected = pendingIds.includes(a.id) && !aDefault;
			const bSelected = pendingIds.includes(b.id) && !bDefault;
			if (aSelected && !bSelected) {
				return -1;
			}
			if (!aSelected && bSelected) {
				return 1;
			}
			return 0;
		});
	}, [territories, search, defaultIds, pendingIds]);

	const hasPendingChanges = useMemo(() => {
		if (pendingIds.length !== selectedIds.length) {
			return true;
		}
		return !pendingIds.every((id) => selectedIds.includes(id));
	}, [pendingIds, selectedIds]);

	const selectionSummary = useMemo(() => {
		const count = selectedIds.length;
		if (count === 0) {
			return translate("document-center.distribution-notification.territory.placeholder");
		}
		if (count === 1) {
			const selectedTerritory = selectedTerritories.find((t) => extractId(t) === selectedIds[0]);
			if (selectedTerritory && typeof selectedTerritory === "object") {
				return selectedTerritory.name;
			}
			const found = territories.find((t) => t.id === selectedIds[0]);
			return found?.name || translate("document-center.distribution-notification.territory.selected-singular");
		}
		return translate("document-center.distribution-notification.territory.selected-plural", { count });
	}, [selectedIds, selectedTerritories, territories]);

	const selectionHint = useMemo(() => {
		if (selectedIds.length === 0) {
			return null;
		}
		return translate("document-center.distribution-notification.territory.select-more-hint");
	}, [selectedIds]);

	const buttonLabel = useMemo(() => {
		if (pendingIds.length === 0 && hasInteracted) {
			return translate("document-center.distribution-notification.territory.clear-selection");
		}
		if (pendingIds.length === 0) {
			return translate("common:btn.close");
		}
		const count = pendingIds.length;
		const key =
			count === 1
				? "document-center.distribution-notification.apply-result"
				: "document-center.distribution-notification.apply-results";
		return translate(key, { count });
	}, [pendingIds, hasInteracted]);

	const handleToggle = (territory) => {
		if (!territory.hasDistributionList) {
			return;
		}
		const isSelected = pendingIds.includes(territory.id);
		let newSelection;
		if (isSelected) {
			newSelection = pendingSelection.filter((t) => extractId(t) !== territory.id);
		} else {
			newSelection = [...pendingSelection, territory];
		}
		setPendingSelection(newSelection);
		setHasInteracted(true);
	};

	const handleApply = () => {
		onChange(pendingSelection);
		setSearch("");
		setIsExpanded(false);
	};

	const renderListContent = () => {
		if (loading) {
			return <div className={styles.loading}>{translate("common:results.loading")}</div>;
		}
		if (filteredTerritories.length === 0) {
			return <div className={styles.emptyState}>{translate("common:empty-state.no-results")}</div>;
		}
		return filteredTerritories.map((territory, index) => {
			const isDisabled = !territory.hasDistributionList;
			const isSelected = pendingIds.includes(territory.id);
			const isDefaultTerritory = defaultIds.includes(territory.id);

			const nextTerritory = filteredTerritories[index + 1];
			const isLastDefault = isDefaultTerritory && nextTerritory && !defaultIds.includes(nextTerritory.id);

			return (
				<React.Fragment key={territory.id}>
					<div
						className={`${styles.listItem} ${isDisabled ? styles.listItemDisabled : ""}`}
						role="button"
						tabIndex={isDisabled ? -1 : 0}
						onClick={() => handleToggle(territory)}
						onKeyDown={(e) => {
							if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
								handleToggle(territory);
							}
						}}
					>
						<Checkbox checked={isSelected} className={styles.checkbox} disabled={isDisabled} size="small" />
						<span
							className={`${styles.itemLabel} ${isSelected ? styles.itemLabelSelected : ""} ${
								isDisabled ? styles.itemLabelDisabled : ""
							}`}
						>
							{territory.name}
						</span>
						{isDisabled && (
							<span className={styles.disabledHint}>
								{translate("document-center.distribution-notification.territory.no-distribution-list")}
							</span>
						)}
					</div>
					{isLastDefault && <hr className={styles.separator} />}
				</React.Fragment>
			);
		});
	};

	return (
		<div className={styles.container}>
			<label className={styles.label} htmlFor="territory-selector-toggle">
				{translate("document-center.distribution-notification.territory.label")}
				<span className={styles.required}>*</span>
			</label>
			<div>
				<div
					className={`${styles.dropdownHeader} ${isExpanded ? styles.dropdownHeaderActive : ""}`}
					id="territory-selector-toggle"
					role="button"
					tabIndex={0}
					onClick={() => !isExpanded && setIsExpanded(true)}
					onKeyDown={(e) => {
						if (!isExpanded && (e.key === "Enter" || e.key === " ")) {
							setIsExpanded(true);
						}
					}}
				>
					{!isExpanded ? (
						<>
							<span
								className={styles[(selectedIds.length === 0 && "placeholder") || "selectionText"]}
								role="button"
								tabIndex={0}
								onClick={() => setIsExpanded(true)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										setIsExpanded(true);
									}
								}}
							>
								{selectionSummary}
								{selectionHint && <span className={styles.selectionHint}> - {selectionHint}</span>}
							</span>
							<IconComponent
								className={styles.chevronIcon}
								icon={icon.faChevronDown}
								onClick={() => setIsExpanded(true)}
							/>
						</>
					) : (
						<>
							<input
								ref={searchInputRef}
								className={styles.searchInput}
								placeholder=""
								type="text"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							<span className={styles.searchIconWrapper}>
								<IconComponent className={styles.searchIcon} icon={icon.faSearch} />
							</span>
							<span
								className={styles.closeChevron}
								role="button"
								tabIndex={0}
								onClick={(e) => {
									e.stopPropagation();
									setPendingSelection(selectedTerritories);
									setSearch("");
									setHasInteracted(false);
									setIsExpanded(false);
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.stopPropagation();
										setPendingSelection(selectedTerritories);
										setSearch("");
										setHasInteracted(false);
										setIsExpanded(false);
									}
								}}
							>
								<IconComponent className={styles.chevronIcon} icon={icon.faChevronDown} />
							</span>
						</>
					)}
				</div>
				<Collapse in={isExpanded}>
					<div className={styles.dropdownPanel}>
						<div className={styles.hintText}>
							{translate("document-center.distribution-notification.territory.search-hint")}
						</div>
						<div className={styles.listContainer}>{renderListContent()}</div>
						{pendingIds.length === 0 && hasInteracted && (
							<div className={styles.clearWarning}>
								{translate("document-center.distribution-notification.territory.clear-warning")}
							</div>
						)}
						<div className={styles.applyFooter}>
							<CustomButton
								fullWidth
								className={pendingIds.length === 0 && !hasInteracted ? styles.closeButton : ""}
								color="primary"
								disabled={pendingIds.length > 0 && !hasPendingChanges}
								variant="contained"
								onClick={handleApply}
							>
								{buttonLabel}
							</CustomButton>
						</div>
					</div>
				</Collapse>
			</div>
		</div>
	);
}

TerritorySelector.propTypes = {
	projectId: PropTypes.number.isRequired,
	selectedTerritories: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.number,
			PropTypes.shape({
				id: PropTypes.number,
				name: PropTypes.string,
			}),
		])
	),
	onChange: PropTypes.func.isRequired,
};

TerritorySelector.defaultProps = {
	selectedTerritories: [],
};
